import { jsx as _jsx } from "react/jsx-runtime";
/**
 * hallmark critique · PhantomLayer
 * tokens locked from docs/design.md §1 (clay-ember) + §10 (single clay accent)
 * states covered: empty (no active phantoms — renders null) · animating (TripsLayer)
 * mobile verified: 320 / 375 / 414 / 768
 * no fake chrome · no italic headers · no shadows · single clay accent per viewport
 *
 * deck.gl TripsLayer overlay integrated into react-leaflet via a small
 * `useMap()` wrapper. Renders each active phantom's `driver_polyline` as
 * an animated line in clay-ember (`#bc7155`, 0.6 opacity, 3px width).
 * Animation loops every 8 seconds — `currentTime` advances via
 * `requestAnimationFrame` at ~30fps.
 *
 * Why this design:
 *   - The slot is rendered INSIDE the react-leaflet `MapContainer`, so
 *     `useMap()` is safe. We subscribe to leaflet's `viewreset`/`zoom`/
 *     `move` events to sync deck.gl's viewState with the map.
 *   - The DeckGL overlay is `position:absolute · pointer-events:none` so
 *     the map keeps intercepting clicks. Phase 7 doesn't need phantom
 *     picking — the strip owns interaction; the map owns visualisation.
 *   - When `activeMatches` is empty, we return null so the DeckGL canvas
 *     doesn't consume WebGL context for nothing.
 *   - TripsLayer needs `getTimestamps` per waypoint — we synthesise
 *     evenly-spaced timestamps `[0, 1, ..., polyline.length-1]`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useMap } from "react-leaflet";
import DeckGL from "@deck.gl/react";
import { TripsLayer } from "@deck.gl/geo-layers";
// Clay-ember (`#bc7155`) at 0.6 opacity — RGBA per design.md §10.
const CLAY_RGBA = [188, 113, 85, 153];
// Animation loop period (ms). One full sweep every 8 seconds.
const LOOP_PERIOD_MS = 8_000;
/**
 * Convert a phantom's `driver_polyline` (backend emits `[lat, lng]`) into
 * deck.gl's `[lng, lat]` convention plus synthetic evenly-spaced timestamps.
 */
function toTripRow(m) {
    const poly = m.driver_polyline;
    if (!poly || poly.length < 2)
        return null;
    const path = poly.map(([lat, lng]) => [lng, lat]);
    const timestamps = path.map((_p, i) => i);
    return {
        id: `${m.id}`,
        path,
        timestamps,
        maxT: path.length - 1,
    };
}
/**
 * Sync a react-leaflet map into a deck.gl viewState. Recomputes on every
 * leaflet move/zoom event, plus once on mount so the first frame is
 * correct.
 */
function useLeafletViewState() {
    const map = useMap();
    const [state, setState] = useState(() => {
        const c = map.getCenter();
        return { longitude: c.lng, latitude: c.lat, zoom: map.getZoom() };
    });
    useEffect(() => {
        const sync = () => {
            const c = map.getCenter();
            setState({ longitude: c.lng, latitude: c.lat, zoom: map.getZoom() });
        };
        map.on("move", sync);
        map.on("zoom", sync);
        map.on("viewreset", sync);
        // Initial sync — leaflet may have re-centered between the useState()
        // initializer running and effects committing.
        sync();
        return () => {
            map.off("move", sync);
            map.off("zoom", sync);
            map.off("viewreset", sync);
        };
    }, [map]);
    return state;
}
export function PhantomLayer({ activeMatches, animate = true, }) {
    const viewState = useLeafletViewState();
    const rafRef = useRef(null);
    const startedAtRef = useRef(0);
    const [currentTime, setCurrentTime] = useState(0);
    const trips = useMemo(() => activeMatches
        .map(toTripRow)
        .filter((t) => t !== null), [activeMatches]);
    // Longest trip drives the loop denominator — every layer wraps by
    // `currentTime % maxT`, so a shared elapsed time reads consistently.
    const globalMaxT = useMemo(() => trips.reduce((acc, t) => Math.max(acc, t.maxT), 0) || 1, [trips]);
    useEffect(() => {
        if (!animate || trips.length === 0) {
            setCurrentTime(0);
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            return;
        }
        startedAtRef.current = performance.now();
        const tick = () => {
            const elapsed = performance.now() - startedAtRef.current;
            // Fraction through the loop [0..1), scaled to path units.
            const frac = (elapsed % LOOP_PERIOD_MS) / LOOP_PERIOD_MS;
            setCurrentTime(frac * globalMaxT);
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [animate, trips.length, globalMaxT]);
    if (trips.length === 0)
        return null;
    const layers = [
        new TripsLayer({
            id: "phantom-trips",
            data: trips,
            // deck.gl's PathLayer accessors — path is [lng, lat] pairs.
            getPath: (d) => d.path,
            getTimestamps: (d) => d.timestamps,
            getColor: CLAY_RGBA,
            opacity: 1, // opacity baked into RGBA
            widthMinPixels: 3,
            widthMaxPixels: 6,
            rounded: true,
            trailLength: Math.max(2, globalMaxT / 2),
            currentTime,
            capRounded: true,
            jointRounded: true,
            updateTriggers: {
                getColor: [CLAY_RGBA.join(",")],
            },
        }),
    ];
    return (_jsx("div", { 
        // Full-cover, click-through, above tile pane but below Leaflet UI (500).
        style: {
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            zIndex: 400,
        }, "data-testid": "phantom-layer", "data-phantom-count": trips.length, children: _jsx(DeckGL, { viewState: viewState, controller: false, layers: layers, style: { width: "100%", height: "100%" } }) }));
}
